import { QueryResult } from '@apollo/client';
import { chain, isEmpty, isNil } from 'lodash';
import InfiniteScroll from 'react-infinite-scroll-component';
import noRecurringOrders from 'src/assets/svg/order/illo-calendar.svg';
import { ActiveSubscriptionsQuery, Exact, InputMaybe } from 'src/gqlReactTypings.generated.d';
import { Loading } from 'src/shared/design-system/Loading';
import { ConsumerSubscriptionsCard } from './ConsumerSubscriptionsCard';
import { EmptyOrdersContainer, OrdersListTitle } from './styles';
import { RecurringSubscription } from './utilities';

type RecurringSubscriptionsTabProps = {
  subscriptionsQueryResult: QueryResult<
    ActiveSubscriptionsQuery,
    Exact<{
      first?: number | undefined;
      after?: InputMaybe<string> | undefined;
    }>
  >;
};

export const RecurringSubscriptionsTab = ({ subscriptionsQueryResult }: RecurringSubscriptionsTabProps) => {
  const { data, loading, fetchMore } = subscriptionsQueryResult;
  if (loading || !data) {
    return <Loading />;
  }
  const { edges, pageInfo } = data.activeSubscriptions;
  if (isEmpty(edges)) {
    return (
      <EmptyOrdersContainer>
        <img src={noRecurringOrders} alt='no subscriptions' />
        <OrdersListTitle>You have no subscriptions.</OrdersListTitle>
      </EmptyOrdersContainer>
    );
  }

  // Only showing subscriptions from recurring orders for now
  // since the UI relies on an actual order to render
  const subscriptions = chain(edges)
    .map('node')
    .filter((s): s is RecurringSubscription => !isNil(s.orderTemplate) && !isNil(s.recurringPeriodDays))
    .value();

  return (
    <InfiniteScroll
      loader={<Loading />}
      dataLength={subscriptions.length}
      hasMore={pageInfo.hasNextPage}
      style={{ overflowY: 'hidden' }}
      next={() => fetchMore({ variables: { after: pageInfo.endCursor } }).catch(console.error)}>
      {subscriptions.map((subscription) => (
        <ConsumerSubscriptionsCard subscription={subscription} />
      ))}
    </InfiniteScroll>
  );
};
